import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  User,
  Calendar as CalendarIcon,
  Clock,
  X,
  Save,
  FileText,
  Phone,
  Mail,
  Check,
  ChevronsUpDown,
  Scissors,
} from 'lucide-react';
import { format, addMinutes, setHours, setMinutes } from 'date-fns';
import { ja } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { ReservationSource, ExtendedReservation } from '../../types/salonboard';
import { SequenceManager, determinePrefix, calculateVisitProcessingDeadline, calculateAmount, calculatePoints } from '../../utils/salonboard-utils';

interface BusinessHoursForDate {
  isOpen: boolean;
  timeSlots: Array<{ start: string; end: string }>;
}

interface BusinessHoursValidation {
  validateReservationTime: (date: string, startTime: string, endTime: string) => {
    errors: string[];
    warnings: string[];
    isValid: boolean;
  };
  isOpenAt: (date: string, time: string) => boolean;
  getBusinessHoursForDate: (date: string) => BusinessHoursForDate | null;
  businessHoursLoading: boolean;
  businessHoursError: string | null;
}

interface NewReservationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (reservation: ExtendedReservation) => void;
  staff: Array<{ id: string; name: string; color: string }>;
  services: Array<{ id: string; name: string; duration: number; price?: number }>;
  customers?: Array<any>;
  businessHoursValidation?: BusinessHoursValidation;
  existingReservations?: ExtendedReservation[];
}

const NewReservationModal: React.FC<NewReservationModalProps> = ({
  open,
  onClose,
  onSave,
  staff,
  services,
  customers = [],
  businessHoursValidation,
  existingReservations = [],
}) => {
  const [formData, setFormData] = useState({
    customerId: null as number | null,
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    date: new Date(),
    startTime: '10:00',
    staffId: '',
    serviceId: '',
    notes: '',
    source: 'PHONE' as ReservationSource,
    amount: 0,
    discount: 0,
    discountReason: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [openCustomerCombobox, setOpenCustomerCombobox] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');

  // 顧客データオプションの変換
  const customerOptions = customers.map(c => ({
    id: c.id,
    label: `${c.name} (${c.phone})`,
    name: c.name,
    phone: c.phone,
    email: c.email,
  }));
  
  // 検索クエリに基づくフィルタリング
  const filteredCustomerOptions = React.useMemo(() => {
    if (!customerSearchQuery) return customerOptions;
    const query = customerSearchQuery.toLowerCase();
    return customerOptions.filter(c => 
      c.name.toLowerCase().includes(query) || 
      c.phone.toLowerCase().includes(query)
    );
  }, [customerOptions, customerSearchQuery]);

  // 時間枠の選択肢（営業時間考慮）
  const getAvailableTimeSlots = () => {
    if (businessHoursValidation) {
      const selectedDateString = format(formData.date, 'yyyy-MM-dd');
      const businessHours = businessHoursValidation.getBusinessHoursForDate(selectedDateString);
      
      if (businessHours && businessHours.isOpen && businessHours.timeSlots.length > 0) {
        // 営業時間に基づいて時間枠を生成
        const slots = [];
        for (const timeSlot of businessHours.timeSlots) {
          const startHour = parseInt(timeSlot.start.split(':')[0]);
          const endHour = parseInt(timeSlot.end.split(':')[0]);
          const endMinute = parseInt(timeSlot.end.split(':')[1]);
          
          for (let hour = startHour; hour <= endHour; hour++) {
            if (hour === endHour && endMinute === 0) break;
            slots.push(`${hour.toString().padStart(2, '0')}:00`);
            if (hour < endHour || (hour === endHour && endMinute >= 30)) {
              slots.push(`${hour.toString().padStart(2, '0')}:30`);
            }
          }
        }
        return [...new Set(slots)].sort();
      }
    }
    
    // フォールバック：デフォルトの時間枠
    const timeSlots = [];
    for (let hour = 9; hour <= 20; hour++) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < 20) {
        timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    return timeSlots;
  };
  
  const timeSlots = getAvailableTimeSlots();

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setFormData(prev => ({ ...prev, date }));
    }
  };

  const handleFieldChange = (field: string, value: string | number | Date | boolean | null) => {
    // Field-specific型変換
    if (field === 'serviceId') {
      const stringValue = value !== null ? String(value) : '';

      // サービス選択時に金額を自動反映（1回のsetFormDataで処理）
      if (stringValue) {
        const selectedService = services.find(s => s.id === stringValue);
        setFormData(prev => ({
          ...prev,
          serviceId: stringValue,
          amount: selectedService?.price || prev.amount || 0
        }));
      } else {
        setFormData(prev => ({ ...prev, serviceId: stringValue }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // 顧客情報の検証
    if (!formData.customerId && !formData.customerName) {
      newErrors.customerId = 'お客様を選択または入力してください';
    }
    
    // 新規顧客の場合の追加検証
    if (formData.customerId === null && formData.customerName) {
      if (!formData.customerPhone) {
        newErrors.customerPhone = '電話番号を入力してください';
      }
    }
    
    if (!formData.staffId) {
      newErrors.staffId = 'スタッフを選択してください';
    }
    if (!formData.serviceId) {
      newErrors.serviceId = 'メニューを選択してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 予約重複チェック関数
  const checkReservationConflict = (
    staffId: string,
    date: Date,
    startTime: string,
    duration: number
  ): { hasConflict: boolean; conflictingReservation?: ExtendedReservation } => {
    const reservationDate = format(date, 'yyyy-MM-dd');
    
    // 終了時間の計算
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const startDate = setMinutes(setHours(new Date(date), startHours), startMinutes);
    const endDate = addMinutes(startDate, duration);
    const endTime = format(endDate, 'HH:mm');
    
    // 同じスタッフ・同じ日付の予約を検索
    for (const reservation of existingReservations) {
      // スタッフIDが異なる場合はスキップ
      if (reservation.staffId !== staffId) continue;
      
      // 日付が異なる場合はスキップ
      const resDate = reservation.date instanceof Date 
        ? format(reservation.date, 'yyyy-MM-dd')
        : String(reservation.date).split('T')[0];
      if (resDate !== reservationDate) continue;
      
      // キャンセル済みの予約はスキップ
      if (reservation.status === 'cancelled') continue;
      
      // 時間重複チェック
      // 新規予約開始時間 < 既存予約終了時間 AND 新規予約終了時間 > 既存予約開始時間
      const existingStart = reservation.startTime;
      const existingEnd = reservation.endTime;
      
      // 時間を分に変換して比較
      const timeToMinutes = (time: string) => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
      };
      
      const newStartMinutes = timeToMinutes(startTime);
      const newEndMinutes = timeToMinutes(endTime);
      const existingStartMinutes = timeToMinutes(existingStart);
      const existingEndMinutes = timeToMinutes(existingEnd);
      
      // 重複判定
      if (newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes) {
        return { hasConflict: true, conflictingReservation: reservation };
      }
    }
    
    return { hasConflict: false };
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const selectedCustomer = formData.customerId ? customerOptions.find(c => c.id === formData.customerId) : null;
    const selectedStaff = staff.find(s => s.id === formData.staffId);
    const selectedService = services.find(s => s.id === formData.serviceId);

    if (!selectedStaff || !selectedService) return;
    
    // 予約重複チェック
    const conflictCheck = checkReservationConflict(
      formData.staffId,
      formData.date,
      formData.startTime,
      selectedService.duration
    );
    
    if (conflictCheck.hasConflict && conflictCheck.conflictingReservation) {
      const conflictRes = conflictCheck.conflictingReservation;
      const conflictMessage = `予約が重複しています。

【既存予約】
お客様: ${conflictRes.customerName}
時間: ${conflictRes.startTime} - ${conflictRes.endTime}
メニュー: ${conflictRes.serviceName}

別の時間帯を選択してください。`;
      alert(conflictMessage);
      return;
    }
    
    // 営業時間検証（オプション）
    if (businessHoursValidation) {
      const reservationDate = format(formData.date, 'yyyy-MM-dd');
      const [hours, minutes] = formData.startTime.split(':').map(Number);
      const startDate = setMinutes(setHours(formData.date, hours), minutes);
      const endDate = addMinutes(startDate, selectedService.duration);
      const endTime = format(endDate, 'HH:mm');
      
      const validation = businessHoursValidation.validateReservationTime(
        reservationDate,
        formData.startTime,
        endTime
      );
      
      if (!validation.isValid) {
        const errorMessages = validation.errors.join('\n');
        alert(`予約時間エラー:\n${errorMessages}`);
        return;
      }
      
      if (validation.warnings.length > 0) {
        const warningMessages = validation.warnings.join('\n');
        const confirmBooking = window.confirm(
          `以下の注意事項があります：\n${warningMessages}\n\n予約を登録しますか？`
        );
        if (!confirmBooking) {
          return;
        }
      }
    }
    
    // 顧客情報の取得（既存顧客または新規顧客）
    const customerInfo = selectedCustomer ? {
      id: selectedCustomer.id,
      name: selectedCustomer.name,
      phone: selectedCustomer.phone,
      email: selectedCustomer.email
    } : {
      id: Date.now(), // 新規顧客の場合は一時的なID
      name: formData.customerName,
      phone: formData.customerPhone,
      email: formData.customerEmail
    };

    // 終了時間の計算
    const [hours, minutes] = formData.startTime.split(':').map(Number);
    const startDate = setMinutes(setHours(formData.date, hours), minutes);
    const endDate = addMinutes(startDate, selectedService.duration);
    
    // 予約番号の生成
    const sequenceManager = SequenceManager.getInstance();
    const prefix = determinePrefix(formData.source);
    const reservationNumber = sequenceManager.getNextReservationNumber(prefix);
    
    // 金額情報の計算
    const servicePrice = selectedService.price || formData.amount || 0;
    const amountInfo = calculateAmount({
      amount: servicePrice,
      discount: formData.discount,
      discountReason: formData.discountReason
    });
    
    // 来店処理期限の計算
    const visitProcessingDeadline = calculateVisitProcessingDeadline(formData.date);

    const newReservation: ExtendedReservation = {
      id: `res-${Date.now()}`,
      reservationNumber,
      source: formData.source,
      customerId: customerInfo.id,
      customerName: customerInfo.name,
      customerPhone: customerInfo.phone,
      customerEmail: customerInfo.email,
      staffId: formData.staffId,
      staffName: selectedStaff.name,
      serviceId: formData.serviceId,
      serviceName: selectedService.name,
      date: formData.date,
      startTime: formData.startTime,
      endTime: format(endDate, 'HH:mm'),
      duration: selectedService.duration,
      status: 'confirmed' as const,
      detailedStatus: 'WAITING_RECEPTION',
      amount: amountInfo,
      visitProcessing: {
        status: 'PENDING',
        deadline: visitProcessingDeadline
      },
      points: {
        earned: calculatePoints(amountInfo),
        used: 0
      },
      cancellationPolicy: {
        type: 'SAME_DAY_50',
        rate: 50
      },
      notes: formData.notes,
      color: selectedStaff.color,
    };

    onSave(newReservation);
    handleReset();
  };

  const handleReset = () => {
    setFormData({
      customerId: null,
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      date: new Date(),
      startTime: '10:00',
      staffId: '',
      serviceId: '',
      notes: '',
      source: 'PHONE' as ReservationSource,
      amount: 0,
      discount: 0,
      discountReason: '',
    });
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            <span>新規予約登録</span>
          </DialogTitle>
          <DialogDescription>
            お客様情報と予約内容を入力してください
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">

          {/* お客様情報 */}
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <User className="h-5 w-5" /> お客様情報
            </h3>

            <div className="grid grid-cols-1 gap-4">
              {/* 顧客検索Combobox */}
              <div className="space-y-2">
                <Label htmlFor="customer-select">
                  お客様を選択または新規入力 *
                </Label>
                <Popover open={openCustomerCombobox} onOpenChange={(open) => {
                  setOpenCustomerCombobox(open);
                  if (!open) {
                    setCustomerSearchQuery('');
                  }
                }}>
                  <PopoverTrigger asChild>
                    <Button
                      id="customer-select"
                      variant="outline"
                      role="combobox"
                      aria-expanded={openCustomerCombobox}
                      className={cn(
                        "w-full justify-between min-h-[44px]",
                        errors.customerId && "border-red-500"
                      )}
                    >
                      <div className="flex items-center gap-2 truncate">
                        <User className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {formData.customerId
                            ? customerOptions.find(c => c.id === formData.customerId)?.label
                            : formData.customerName || ""}
                        </span>
                      </div>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="お客様名または電話番号で検索..."
                        value={customerSearchQuery}
                        onValueChange={(value) => {
                          setCustomerSearchQuery(value);
                          handleFieldChange('customerName', value);
                          // 既存顧客が見つからない場合はcustomerIdをクリア
                          if (!value || !customerOptions.find(c =>
                            c.name.toLowerCase().includes(value.toLowerCase()) ||
                            c.phone.includes(value)
                          )) {
                            handleFieldChange('customerId', null);
                          }
                        }}
                        autoFocus
                      />
                      <CommandList>
                        <CommandEmpty>
                          <div className="p-4 text-sm">
                            <p className="text-muted-foreground mb-2">該当するお客様が見つかりません</p>
                            {customerSearchQuery && (
                              <>
                                <p className="text-xs text-muted-foreground mb-3">
                                  「{customerSearchQuery}」で新規顧客として登録できます
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => {
                                    handleFieldChange('customerName', customerSearchQuery);
                                    handleFieldChange('customerId', null);
                                    setOpenCustomerCombobox(false);
                                  }}
                                >
                                  <User className="mr-2 h-4 w-4" />
                                  新規顧客として確定
                                </Button>
                              </>
                            )}
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {filteredCustomerOptions.map((customer) => (
                            <CommandItem
                              key={customer.id}
                              value={customer.label}
                              onSelect={() => {
                                handleFieldChange('customerId', customer.id);
                                handleFieldChange('customerName', customer.name);
                                handleFieldChange('customerPhone', customer.phone);
                                handleFieldChange('customerEmail', customer.email);
                                setCustomerSearchQuery('');
                                setOpenCustomerCombobox(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.customerId === customer.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{customer.name}</span>
                                <span className="text-xs text-muted-foreground">{customer.phone}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.customerId && (
                  <p className="text-sm text-red-500">{errors.customerId}</p>
                )}
                {!errors.customerId && (
                  <p className="text-sm text-muted-foreground">
                    既存のお客様を選択するか、新規のお客様名を入力してください
                  </p>
                )}
              </div>
            
              {/* 新規顧客の場合のみ追加情報入力フィールドを表示 */}
              {formData.customerId === null && formData.customerName && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer-phone">
                      電話番号 *
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="customer-phone"
                        type="tel"
                        placeholder="090-1234-5678"
                        value={formData.customerPhone}
                        onChange={(e) => handleFieldChange('customerPhone', e.target.value)}
                        className={cn(
                          "pl-10 min-h-[44px]",
                          errors.customerPhone && "border-red-500"
                        )}
                      />
                    </div>
                    {errors.customerPhone && (
                      <p className="text-sm text-red-500">{errors.customerPhone}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="customer-email">
                      メールアドレス
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="customer-email"
                        type="email"
                        placeholder="example@email.com"
                        value={formData.customerEmail}
                        onChange={(e) => handleFieldChange('customerEmail', e.target.value)}
                        className="pl-10 min-h-[44px]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 予約詳細 */}
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <CalendarIcon className="h-5 w-5" /> 予約詳細
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* 予約日選択 */}
              <div className="space-y-2">
                <Label htmlFor="reservation-date">
                  予約日 *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="reservation-date"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal min-h-[44px]",
                        !formData.date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date ? (
                        format(formData.date, 'yyyy年M月d日(E)', { locale: ja })
                      ) : (
                        <span>日付を選択</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] overflow-hidden p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => {
                        if (date) {
                          handleDateChange(date);
                        }
                      }}
                      locale={ja}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            
              {/* 開始時間選択 */}
              <div className="space-y-2">
                <Label htmlFor="start-time">
                  開始時間 *
                </Label>
                <Select
                  value={formData.startTime}
                  onValueChange={(value) => handleFieldChange('startTime', value)}
                >
                  <SelectTrigger id="start-time" className="min-h-[44px]">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="時間を選択" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {timeSlots.map(time => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* スタッフ選択 */}
              <div className="space-y-2">
                <Label htmlFor="staff-select">
                  担当スタッフ *
                </Label>
                <Select
                  value={formData.staffId}
                  onValueChange={(value) => handleFieldChange('staffId', value)}
                >
                  <SelectTrigger
                    id="staff-select"
                    className={cn("min-h-[44px]", errors.staffId && "border-red-500")}
                  >
                    <SelectValue placeholder="スタッフを選択" />
                  </SelectTrigger>
                  <SelectContent className="min-w-[280px]">
                    {staff.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: s.color }}
                          />
                          <span>{s.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.staffId && (
                  <p className="text-sm text-red-500">{errors.staffId}</p>
                )}
              </div>

              {/* サービス選択 */}
              <div className="space-y-2">
                <Label htmlFor="service-select">
                  メニュー *
                </Label>
                <Select
                  value={formData.serviceId}
                  onValueChange={(value) => handleFieldChange('serviceId', value)}
                >
                  <SelectTrigger
                    id="service-select"
                    className={cn("min-h-[44px]", errors.serviceId && "border-red-500")}
                  >
                    <div className="flex items-center gap-2 overflow-hidden w-full text-left">
                      <Scissors className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 truncate">
                        <SelectValue placeholder="メニューを選択" />
                      </div>
                    </div>
                  </SelectTrigger>
                  <SelectContent className="min-w-[320px]">
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name} ({service.duration}分)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.serviceId && (
                  <p className="text-sm text-red-500">{errors.serviceId}</p>
                )}
              </div>

              {/* 予約経路選択 */}
              <div className="space-y-2">
                <Label htmlFor="source-select">
                  予約経路
                </Label>
                <Select
                  value={formData.source}
                  onValueChange={(value) => handleFieldChange('source', value as ReservationSource)}
                >
                  <SelectTrigger id="source-select" className="min-h-[44px]">
                    <SelectValue placeholder="予約経路を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PHONE">電話予約</SelectItem>
                    <SelectItem value="HOT_PEPPER_BEAUTY">ホットペッパービューティー</SelectItem>
                    <SelectItem value="WEBSITE">自社サイト</SelectItem>
                    <SelectItem value="WALK_IN">飛び込み</SelectItem>
                    <SelectItem value="OTHER">その他</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 金額入力 */}
              <div className="space-y-2">
                <Label htmlFor="amount-input">
                  金額
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">¥</span>
                  <Input
                    id="amount-input"
                    type="number"
                    placeholder="0"
                    value={formData.amount || ''}
                    onChange={(e) => handleFieldChange('amount', parseInt(e.target.value) || 0)}
                    className="pl-8 min-h-[44px]"
                  />
                </div>
              </div>

              {/* 割引額入力 */}
              <div className="space-y-2">
                <Label htmlFor="discount-input">
                  割引額
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">¥</span>
                  <Input
                    id="discount-input"
                    type="number"
                    placeholder="0"
                    value={formData.discount || ''}
                    onChange={(e) => handleFieldChange('discount', parseInt(e.target.value) || 0)}
                    className="pl-8 min-h-[44px]"
                  />
                </div>
              </div>

              {/* 割引理由入力 */}
              <div className="space-y-2">
                <Label htmlFor="discount-reason-input">
                  割引理由
                </Label>
                <Input
                  id="discount-reason-input"
                  type="text"
                  placeholder="初回割引、クーポン利用など"
                  value={formData.discountReason}
                  onChange={(e) => handleFieldChange('discountReason', e.target.value)}
                  className="min-h-[44px]"
                />
              </div>

              {/* 予約メモ */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="notes-textarea">
                  予約メモ
                </Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Textarea
                    id="notes-textarea"
                    placeholder="アレルギー情報、特別な要望など"
                    value={formData.notes}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    className="pl-10 min-h-[88px] resize-none"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 予約確認表示 */}
          {formData.customerId && formData.serviceId && formData.staffId && (
            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-base">予約内容確認</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm">{customerOptions.find(c => c.id === formData.customerId)?.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm">{format(formData.date, 'yyyy年M月d日 (E)', { locale: ja })}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm">{formData.startTime}〜</span>
                </div>
                <div className="flex items-center gap-3">
                  <Scissors className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{services.find(s => s.id === formData.serviceId)?.name}</span>
                    <span className="text-xs text-muted-foreground">担当: {staff.find(s => s.id === formData.staffId)?.name}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="min-h-[44px] w-full sm:w-auto"
          >
            <X className="mr-2 h-4 w-4" />
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            className="min-h-[44px] w-full sm:w-auto"
          >
            <Save className="mr-2 h-4 w-4" />
            予約登録
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewReservationModal;